import {
  DesignTokenSize,
  DesignTokenSizeEnum,
} from '@volkswagen-onehub/components-core';
import {
  Children,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AutoScrollInner,
  StyledAutoScrollContainer,
  StyledAutoScrollItem,
} from './AutoScrollContainer.styles';

export enum Breakpoint {
  MOBILE = 'mobile',
  DESKTOP = 'desktop',
}

export interface AutoScrollContainerProps {
  activeSlideIndex: number;
  slidesPerPage?: number;
  gap?: DesignTokenSize;
  responsiveProps?: AutoScrollContainerResponsiveProps;
  children: ReactNode;
}

export interface AutoScrollContainerResponsiveProps {
  [Breakpoint.MOBILE]: {
    slidesPerPage?: number;
    gap?: DesignTokenSize;
  };
  [Breakpoint.DESKTOP]: {
    slidesPerPage?: number;
    gap?: DesignTokenSize;
  };
}

function AutoScrollContainer({
  activeSlideIndex,
  slidesPerPage = 2,
  gap = DesignTokenSizeEnum.static150,
  responsiveProps = {
    mobile: {
      slidesPerPage: 2,
      gap: DesignTokenSizeEnum.static150,
    },
    desktop: {
      slidesPerPage: 3,
      gap: DesignTokenSizeEnum.static300,
    },
  },
  children,
}: AutoScrollContainerProps) {
  const [activeSlideOffsetLeft, setActiveSlideOffsetLeft] = useState(0);
  const childrenRef = useRef<HTMLDivElement[]>([]);

  const lengthOfChildren = Children.count(children);

  const resetResponsiveProps = { ...responsiveProps };

  const setSlidesPerPage = (breakpoint: Breakpoint) => {
    if (
      lengthOfChildren < (resetResponsiveProps[breakpoint].slidesPerPage || 0)
    ) {
      resetResponsiveProps[breakpoint].slidesPerPage = lengthOfChildren;
    }
  };

  setSlidesPerPage(Breakpoint.MOBILE);
  setSlidesPerPage(Breakpoint.DESKTOP);

  const slideWidth = {
    mobile: getSlideWidth(
      Breakpoint.MOBILE,
      resetResponsiveProps,
      slidesPerPage
    ),
    desktop: getSlideWidth(
      Breakpoint.DESKTOP,
      resetResponsiveProps,
      slidesPerPage
    ),
  };

  const slideGap = {
    mobile: getGap(Breakpoint.MOBILE, resetResponsiveProps, gap),
    desktop: getGap(Breakpoint.DESKTOP, resetResponsiveProps, gap),
  };

  useEffect(() => {
    if (!childrenRef.current[activeSlideIndex]) return;

    const distanceFromLeft = childrenRef.current[activeSlideIndex].offsetLeft;

    setActiveSlideOffsetLeft(distanceFromLeft);
  }, [activeSlideIndex]);

  return (
    <StyledAutoScrollContainer>
      <AutoScrollInner
        activeSlideOffsetLeft={activeSlideOffsetLeft}
        slideWidthMobile={slideWidth.mobile}
        slideWidthDesktop={slideWidth.desktop}
        gapMobile={slideGap.mobile}
        gapDesktop={slideGap.desktop}
        childrenCount={lengthOfChildren}
      >
        {Children.map(children, (child, index) => {
          if (isValidElement(child)) {
            return (
              <StyledAutoScrollItem
                key={`auto-scroll-item-${index}`}
                ref={(ref: HTMLDivElement) =>
                  (childrenRef.current[index] = ref)
                }
              >
                {child}
              </StyledAutoScrollItem>
            );
          }
          return child;
        })}
      </AutoScrollInner>
    </StyledAutoScrollContainer>
  );
}

function getSlideWidth(
  breakpoint: keyof AutoScrollContainerResponsiveProps,
  responsiveProps: AutoScrollContainerResponsiveProps | undefined,
  fallback: number
) {
  if (!responsiveProps?.[breakpoint]?.slidesPerPage) return 100 / fallback;
  return 100 / responsiveProps[breakpoint].slidesPerPage!;
}

function getGap(
  breakpoint: keyof AutoScrollContainerResponsiveProps,
  responsiveProps: AutoScrollContainerResponsiveProps | undefined,
  fallback: DesignTokenSize
) {
  if (!responsiveProps?.[breakpoint]?.gap) return fallback;
  return responsiveProps[breakpoint].gap!;
}

export default AutoScrollContainer;
